import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable, catchError, from, of, switchMap, take } from 'rxjs';
import { LoanService } from 'src/app/services/loan/loan.service';
import { ApplicationContextService } from 'src/app/services/application-context.service';
import Swal from 'sweetalert2';

/**
 * Stops a TPO user from opening disclosures when the company requires a PMI quote, the loan meets
 * the Mortgage Insurance expression, and no quote has been applied. Runs before the disclosures
 * page loads, so a blocked loan never auto-creates an LOS file on the way in.
 */
@Injectable({ providedIn: 'root' })
export class CanActivatePmiQuoteBeforeDisclosuresGuard {
  constructor(
    private readonly _applicationContextService: ApplicationContextService,
    private readonly _loanService: LoanService,
    private readonly _router: Router
  ) { }

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    if (!state.url.startsWith('/tpo')) {
      return of(true);
    }

    const applicationId = this.getApplicationId(route);
    if (applicationId === null || Number.isNaN(applicationId)) {
      return of(false);
    }

    return this._applicationContextService.context.pipe(
      take(1),
      switchMap(context => {
        if (!context?.isTpo || !context.tpoConfig?.requirePmiQuoteBeforeDisclosures) {
          return of(true);
        }

        return this._loanService.isPmiQuoteRequiredBeforeDisclosures(applicationId).pipe(
          switchMap(result => result?.pmiQuoteRequired
            ? from(this.stopForMissingPmiQuote(applicationId))
            : of(true as boolean | UrlTree)),
          catchError(() => from(this.stopBecausePmiStatusIsUnknown()))
        );
      })
    );
  }

  private async stopForMissingPmiQuote(applicationId: number): Promise<UrlTree> {
    await Swal.fire({
      title: 'PMI Required',
      text: 'Current loan details require PMI to be run prior to disclosing.',
      icon: 'warning',
      confirmButtonText: 'Run PMI Quote',
      allowOutsideClick: false,
      allowEscapeKey: false,
      showCancelButton: false,
    });

    return this._router.createUrlTree(['/tpo/app-details', applicationId, 'mi-quoting']);
  }

  private async stopBecausePmiStatusIsUnknown(): Promise<false> {
    await Swal.fire({
      title: 'Unable to check PMI',
      text: 'Disclosures stay blocked until PMI status can be verified.',
      icon: 'error',
      confirmButtonText: 'OK',
      allowOutsideClick: false,
      allowEscapeKey: false,
    });

    return false;
  }

  private getApplicationId(route: ActivatedRouteSnapshot): number | null {
    let current: ActivatedRouteSnapshot | null = route;
    while (current) {
      const rawId = current.params['id'];
      if (rawId !== undefined && rawId !== null && rawId !== '') {
        const id = Number(rawId);
        return Number.isInteger(id) && id > 0 ? id : Number.NaN;
      }
      current = current.parent;
    }
    return null;
  }
}
